import { createTheme } from '@mui/material/styles';
import fonts from './assets/sass/fonts.scss';
import palette from './assets/sass/palette.scss';

// Fallback values for testing or when SCSS modules fail to load
const fallbackPalette = {
  primary: '#8953EF',
  secondary: '#0F0F0F',
  grey: '#837F8A',
  lightGrey: '#EDEEF0',
  mediumGrey: '#4C4C4C',
  darkGrey: '#374151',
  borderGrey: '#9CAC99',
  borderLightGrey: '#E3deed',
  softPink: '#FCFBFF',
  borderPink: '#E3DCE7',
  backgroundPink: '#ede1fe',
  greenSharp: '#1FCC92',
  lightBlue: '#A5F0FC',
  darkBlue: '#225CF2',
  mediumBlue: '#2E84FF',
  lightPurple: '#C1A0FF',
  mediumPurple: '#B38AFF',
  darkPurple: '#4F22A3',
  palePurple: '#F9EDFF',
  lightRed: '#FFA1A1',
  errorRed: '#CB2020',
  lime: '#ABE832',
  mediumLime: '#7EB11C',
  darkGreen: '#112C11',
  paleGreen: '#E9FFEA',
  darkSand: '#5F5946',
  lightSand: '#D1CAB6',
  white: '#F7F6F1',
};

const fallbackFonts = {
  fontFactorRegular: '"FactorA Regular", arial, sans-serif',
  fontFactorMedium: '"FactorA Medium", arial, sans-serif',
  fontFactorBold: '"FactorA Bold", arial, sans-serif',
  fontFactorExtraBold: '"FactorA ExtraBold", arial, sans-serif',
};

// Use actual imports or fallbacks
const paletteValues = (palette && typeof palette === 'object' && palette.primary) ? palette : fallbackPalette;
const fontValues = (fonts && typeof fonts === 'object' && fonts.fontFactorRegular) ? fonts : fallbackFonts;

// Create a theme instance
const theme = createTheme({
  palette: {
    primary: {
      main: paletteValues.primary,
      light: '#d7c5f9',
      dark: '#5421db',
      contrastText: '#ffffff',
    },
    secondary: {
      main: paletteValues.secondary,
      light: '#a8a8a8',
      dark: paletteValues.secondary,
      contrastText: '#ffffff',
    },
    background: {
      default: paletteValues.softPink,
      paper: '#ffffff',
    },
    text: {
      primary: paletteValues.secondary,
      secondary: paletteValues.mediumGrey,
    },
    error: {
      main: '#d32f2f',
      light: 'rgba(211, 47, 47, 0.8)',
    },
  },
  typography: {
    fontFamily: fontValues.fontFactorRegular,
    h1: {
      fontFamily: fontValues.fontFactorBold,
      fontWeight: 700,
    },
    h2: {
      fontFamily: fontValues.fontFactorBold,
      fontSize: '40px',
      fontWeight: 700
    },
    h3: {
      fontFamily: fontValues.fontFactorBold,
      fontSize: '32px',
      fontWeight: 700,
    },
    h5: {
      fontFamily: fontValues.fontFactorMedium,
      fontWeight: 500,
    },
    button: {
      textTransform: 'none',
    },
    headerLink: {
      color: 'black',
      textDecoration: 'none',
      padding: '16px',
    },
    body: {
      color: paletteValues.secondary
    },
    small: {
      color: paletteValues.grey
    },
    sideBarStats: {
      color: 'black',
      fontFamily: fontValues.fontFactorMedium,
    },
    sideBarStatsFunky: {
      color: 'black',
      fontFamily: fontValues.fontFactorMedium,
      fontFeatureSettings: '"ss01" 1'
    },
    strong: {
      fontWeight: 700
    },
    medium: {
      fontWeight: 600
    },
    bodyGrey: {
      color: paletteValues.mediumGrey,
    },
    emPurple: {
      color: paletteValues.primary,
    },
    fieldLabel: {
      fontWeight: '700'
    },
    factorA: {
      fontFamily: fontValues.fontFactorMedium,
      fontFeatureSettings: '"ss01" 1'
    },
    factorExtraBold: {
      fontFamily: fontValues.fontFactorExtraBold,
    },
    stat: {
      fontSize: '24px',
      fontWeight: '700',
      fontFamily: fontValues.fontFactorMedium
    },
    pageHeader: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: paletteValues.darkGrey
    },
    formHeader: {
      fontSize: '18px',
      fontWeight: '700'
    },
    sectionHeader: {
      fontWeight: '700',
      fontSize: '32px',
      paddingBottom: '16px'
    },
    moduleHeader: {
      fontSize: '20px',
      fontWeight: 700,
      marginBottom: '16px'
    }
  },
  shape: {
    borderRadius: 15,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          padding: '24px',
        },
      },
      variants: [
        {
          props: { variant: 'colorful' },
          style: {
            background: 'linear-gradient(45deg, #D84C6F 30%, #E06F3E 90%)',
            color: 'white',
            marginTop: '32px',
          },
        },
        {
          props: { variant: 'pendingActions' },
          style: {
            borderLeft: '4px solid',
            borderColor: '#d32f2f',
            marginBottom: '24px',
          },
        },
        {
          props: { variant: 'profile' },
          style: {
            boxShadow: '10px 0 20px 0 rgba(0,0,0,.03)',
            width: '100%',
            minHeight: '100vh',
            padding: '48px',
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            backgroundColor: paletteValues.darkGreen,
            color: paletteValues.lightSand,
            fontFamily: fontValues.fontFactorMedium,
          }
        },
        {
          props: { variant: 'rightPane' },
          style: {
            background: 'transparent',
            borderRadius: '0',
            // margin: '48px 0 !important',
            padding: 0,
          }
        },
        {
          props: { variant: 'gameBox' },
          style: {
            width: '100%', 
            padding: '16px', 
            border: `1px solid ${paletteValues.borderPink}`,
            marginBottom: '24px'
          }
        }
      ],
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          border: 'none',
          borderBottom: `1px solid ${paletteValues.borderPink}`,
          boxShadow: 'none'
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          variants: [
            {
              props: { variant: 'gameCard' },
              style: {
                boxShadow: 'none',
                border: `1px solid ${paletteValues.borderPink}`,
                display: 'flex',
                flexDirection: 'row',
                padding: '0 0 16px 0',
                marginTop: '24px',
                borderRadius: '32px'
              }
            }
          ]
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          '&.Mui-disabled img': {
            opacity: 0.5
          }
        },
      },
      variants: [
        {
          props: { variant: 'text'},
          style: {
            backgroundColor: 'transparent',
            color: paletteValues.secondary,
            fontWeight: '700',
            paddingRight: 0,
            paddingLeft: 0
          }
        },
        {
          props: { variant: "link" },
          style: {
            color: paletteValues.primary,
            fontWeight: 400,
            textDecoration: 'underline',
            padding: 0
          }
        },
        {
          props: { variant: 'login' },
          style: {
            backgroundColor: 'white',
            color: 'black',
            width: '100%',
            marginTop: '8px',
          },
        },
        {
          props: { variant: 'contained', color: 'loginSubmit' },
          style: {
            marginTop: '24px',
            marginBottom: '16px',
            backgroundColor: 'white',
            color: '#E06F3E',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.9)'
            }
          },
        },
        {
          props: { variant: 'activityAction' },
          style: {
            marginLeft: 'auto',
          },
        },
        {
          props: { variant: 'gameDelete' },
          style: {
            position: 'absolute',
            bottom: '10px',
            right: '10px',
            backgroundColor: 'white',
            borderColor: '#d32f2f',
            color: '#d32f2f',
            '&:hover': {
              backgroundColor: 'rgba(211, 47, 47, 0.08)',
              borderColor: '#b71c1c',
            }
          },
        },
        {
          props: { variant: 'containedWagl' },
          style: {
            backgroundColor: paletteValues.primary,
            borderWidth: '1px',
            borderColor: paletteValues.borderPink,
            color: 'white',
            fontFamily: 'FactorA Medium, Verdana, sans-serif',
            fontFeatureSettings: '"ss01" 1',
            width: '100%'
          }
        },
        {
          props: { variant: 'containedWaglOutline' },
          style: {
            // backgroundColor: paletteValues.primary,
            borderWidth: '1px',
            borderColor: paletteValues.primary,
            color: paletteValues.primary,
            fontFamily: 'FactorA Medium, Verdana, sans-serif',
            fontFeatureSettings: '"ss01" 1',
            width: '100%'
          }
        },
        {
          props: { variant: 'containedNew' },
          style: {
            backgroundColor: paletteValues.primary,
            borderWidth: '1px',
            borderColor: paletteValues.primary,
            color: 'white',
            fontFamily: 'FactorA Medium, Verdana, sans-serif',
            // fontFeatureSettings: '"ss01" 1',
            width: '100%',
            height: '68px',
            borderRadius: '34px',
          }
        },
        {
          props: { variant: 'containedNewOutline' },
          style: {
            // backgroundColor: paletteValues.primary,
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'black',
            color: 'black',
            fontFamily: 'FactorA Medium, Verdana, sans-serif',
            // fontFeatureSettings: '"ss01" 1',
            width: '100%',
            height: '68px',
            borderRadius: '34px',
          }
        },
        {
          props: { variant: 'groupStatus' },
          style: {
            padding: '3px 8px 4px',
            fontSize: '12px',
            borderRadius: '6px',
            height: '24px'
          }
        }
      ],
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          opacity: '0.75',
          '&.MuiToggleButtonGroup-firstButton, &.MuiToggleButtonGroup-middleButton, &.MuiToggleButtonGroup-lastButton': {
            borderRadius: '8px',
            border: `1px solid ${paletteValues.borderLightGrey}`
          },
          '&.MuiToggleButtonGroup-firstButton, &.MuiToggleButtonGroup-middleButton': {
            marginRight: '16px'
          },
          '&.Mui-selected': {
            opacity: '1.0',
            borderColor: paletteValues.secondary
          }
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          marginBottom: '16px',
        },
        standardError: {
          backgroundColor: 'rgba(211, 47, 47, 0.8)',
          color: 'white',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: 'inherit',
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        container: {
          '&.homeContainer': {
            marginTop: '24px',
          },
        },
      },
    },
    // MuiTextField: {
    //   styleOverrides: {
    //     root: {
    //       backgroundColor: 'white',
    //       borderColor: paletteValues.borderPink,
    //       borderRadius: '15px',
    //       boxShadow: '0 0 30px 10px rgba(0,0,0,0.05)',
    //       '--TextField-brandBorderColor': paletteValues.borderPink,
    //       '&::placeholder': {
    //         color: paletteValues.mediumGrey
    //       },
    //       '& label.Mui-shrink': {
    //         color: 'transparent',
    //       },
    //       '& input': {
    //         padding: '32px 24px'
    //       }
    //     }
    //   },
    //   variants: [
    //     {
    //       props: { variant: 'outlined', color: 'loginField' },
    //       style: {
    //         input: { color: 'white', backgroundColor: 'white' },
    //         label: { color: 'rgba(255,255,255,0.7)' },
    //         '& .MuiOutlinedInput-root': {
    //           '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
    //           '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
    //           '&.Mui-focused fieldset': { borderColor: 'white' }
    //         }
    //       },
    //     },
    //   ],
    // },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: 'white',
          borderColor: paletteValues.borderLightGrey,
          borderRadius: '15px',
          '--TextField-brandBorderColor': paletteValues.borderLightGrey,
          '&::placeholder': {
            color: paletteValues.mediumGrey
          },
          '& label.Mui-shrink': {
            color: 'transparent',
          },
          '& input': {
            // padding: '32px 24px'
          },
          // '& fieldset': {
          //   borderColor: 'rgba(255,255,255,0.3)'
          // },
          // '&:hover fieldset': {
          //   borderColor: 'rgba(255,255,255,0.5)'
          // },
          '&.Mui-focused fieldset': {
            borderColor: paletteValues.borderPink,
            boxShadow: `0 0 30px 10px ${paletteValues.backgroundPink}`,
          }
        }
      }
    },
    MuiCheckbox: {
      variants: [
        {
          props: { variant: "bigCheckbox" },
          style: {
            '& .MuiSvgIcon-root': {
              fontSize: '48px'
            },
            '&.Mui-checked .MuiSvgIcon-root': {
              color: paletteValues.secondary,
              fill: paletteValues.secondary
            }
          }
        }
      ]
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          '& .MuiSlider-valueLabel': {
            backgroundColor: paletteValues.backgroundPink,
            color: paletteValues.secondary
          },
          '& .MuiSlider-valueLabel::before': {
            backgroundColor: paletteValues.backgroundPink
          }
        }
      }
    },
    MuiTypography: {
      variants: [
        {
          props: { variant: 'body2', color: 'loginLink' },
          style: {
            marginTop: '16px',
            color: 'white',
          },
        },
        {
          props: { variant: 'activityMessage' },
          style: {
            fontWeight: 'medium',
          },
        },
        {
          props: { variant: 'activityTime' },
          style: {
            marginRight: '8px',
          },
        },
        {
          props: { variant: 'headerSignOut' },
          style: {
            background: 'none',
            border: 'none',
            color: 'inherit',
            cursor: 'pointer',
            font: 'inherit',
            padding: 0
          }
        },
      ],
    },
    MuiAvatar: {
      variants: [
        {
          props: { variant: 'activity' },
          style: {
            marginRight: '16px',
            backgroundColor: '#D84C6F',
            color: 'white',
            '& .MuiSvgIcon-root': {
              color: 'white',
            },
          },
        },
        {
          props: { variant: 'urgentActivity' },
          style: {
            marginRight: '16px',
            backgroundColor: '#d32f2f',
            color: 'white',
            '& .MuiSvgIcon-root': {
              color: 'white',
            },
          },
        },
        {
          props: { variant: 'profileAvatar' },
          style: {
            width: '48px',
            height: '48px'
          }
        }
      ],
    },
    MuiListItem: {
      variants: [
        {
          props: { variant: 'activity' },
          style: {
            marginBottom: '8px',
            borderRadius: '16px',
          },
        },
        {
          props: { variant: 'urgentActivity' },
          style: {
            marginBottom: '8px',
            borderRadius: '16px',
            backgroundColor: 'rgba(216, 76, 111, 0.05)',
          },
        }
      ],
    },
    MuiListItemAvatar: {
      variants: [
        {
          props: { variant: 'inviteAvatar' },
          style: {
            minWidth: '48px',
            margin: { xs: '8px', md: 0 },
            marginLeft: { xs: 'unset', md: '-12px' }
          }
        }
      ]
    },
    MuiChip: {
      variants: [
        {
          props: { variant: 'inviteChip' },
          style: {
            backgroundColor: paletteValues.primary,
            opacity: 0.12,
          }
        },
        {
          props: { variant: 'urgentBadge' },
          style: {
            marginRight: '8px',
          },
        },
        {
          props: { variant: 'cancelledGame' },
          style: {
            position: 'absolute',
            bottom: '10px',
            right: '10px',
          },
        },
        {
          props: { variant: 'gameDate' },
          style: {
            backgroundColor: paletteValues.backgroundPink,
            borderRadius: '4px',
            '&::hover': {
              backgroundColor: paletteValues.primary,
              color: 'white'
            }
          }
        },
        {
          props: { variant: 'statusAvailable' },
          style: {
            backgroundColor: paletteValues.lightBlue,
            color: paletteValues.darkBlue,
          }
        },
        {
          props: { variant: 'statusInvited' },
          style: {
            backgroundColor: paletteValues.lightPurple,
            color: paletteValues.darkPurple
          }
        },
        {
          props: { variant: 'statusDeclined' },
          style: {
            backgroundColor: paletteValues.lightRed,
            color: paletteValues.errorRed
          }
        },
        {
          props: { variant: 'groupChip' },
          style: {
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '600',
            padding: '4px 8px',
            margin: '0 auto',
            height: '20px',
            '& .MuiChip-label': {
              padding: 0
            }
          }
        },
        {
          props: { variant: 'rankChip'},
          style: {
            backgroundColor: paletteValues.primary, 
            color: 'white', 
            fontWeight: 'bold', 
            fontFamily: '"FactorA Medium", arial, sans-serif', 
            padding: '2px 4px', 
            height: '14px',
            '& .MuiChip-label': { 
              fontSize: '10px',
              padding: 0 
            }
          }
        }
      ],
    },
    MuiBox: {
      variants: [
        {
          props: { variant: 'activityContainer' },
          style: {
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '24px',
          },
        },
        {
          props: { variant: 'activityHeader' },
          style: {
            display: 'flex',
            alignItems: 'center',
            marginBottom: '16px',
          },
        },
        {
          props: { variant: 'activityContent' },
          style: {
            width: '100%',
          },
        },
        {
          props: { variant: 'activityFooter' },
          style: {
            display: 'flex',
            alignItems: 'center',
            marginTop: '8px',
          },
        },
        {
          props: { variant: 'settingSection' },
          style: {
            borderBottom: `1px solid ${paletteValues.borderPink}`
          }
        }
      ],
    },
    MuiStack: {
      variants: [
        {
          props: { variant: 'profileScores' },
          style: {
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            justifyContent: 'center',
            // border: `1px solid ${paletteValues.borderGrey}`,
            padding: '10px',
            color: 'black'
          }
        }
      ]
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          marginTop: '8px',
          minWidth: '200px',
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          padding: '12px 24px',
          '&:hover': {
            backgroundColor: 'rgba(216, 76, 111, 0.08)', // Using primary color with opacity
          }
        }
      }
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: paletteValues.borderPink
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: 0,
          padding: "6px 0",
          fontFamily: '"FactorA Medium", arial, sans-serif'
        }
      }
    }
  },
});

export default theme; 